import React, { useEffect, useState, useRef } from "react";
import UseChat from "../utils/use-chat";
import clsx from "clsx";
import axiosInstance from "../utils/axios-instance";
import UseAuth from "../utils/use-auth";
import { chatStarters } from "../utils/data/chat-starters";
import { FaPaperPlane, FaSpinner, FaTimes, FaFilePdf, FaFileWord } from 'react-icons/fa';
import { AiOutlinePaperClip } from 'react-icons/ai';
import { RiRobot2Fill } from "react-icons/ri";
import { RiUserFill } from "react-icons/ri";
import { marked } from "marked";
import { ExtractDocumentText } from "../utils/extract-document-text";

const ChatComponent = () => {
  const { currentConversation, handleSelectConversation } = UseChat();
  const { user } = UseAuth();
  const [messages, setMessages] = useState({ totalCount: 0, data: [] });
  const [newMessage, setNewMessage] = useState("");
  const [loadingState, setLoadingState] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [extractedText, setExtractedText] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [fileName, setFileName] = useState("");
  const messagesEndRef = useRef(null);
  const [streamedText, setStreamedText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [fullMessage, setFullMessage] = useState("");
  const streamInterval = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamedText]);

  useEffect(() => {
    const findMessages = async () => {
      try {
        const res = await axiosInstance(`/chat/find/messages/${currentConversation?.id}`);
        setMessages(res.data);
      } catch (error) {}
    };
    currentConversation?.id && findMessages();
  }, [currentConversation?.id]);

  useEffect(() => {
    return () => { if (streamInterval.current) clearInterval(streamInterval.current); };
  }, []);

  const getProcessedStreamText = () => {
    try {
      let processedText = streamedText;
      const codeBlockCount = (streamedText.match(/```/g) || []).length;
      if (codeBlockCount % 2 !== 0) processedText += "\n```";
      const asteriskCount = (streamedText.match(/\*/g) || []).length;
      if (asteriskCount % 2 !== 0) processedText += "*";
      if (streamedText.includes('[') && !streamedText.includes(']')) processedText += "]";
      return marked(processedText);
    } catch (error) {
      return streamedText;
    }
  };

  const simulateStreaming = (text) => {
    setFullMessage(text);
    setStreamedText("");
    setIsStreaming(true);
    const tokens = text.split(/(?<=\s+)/);
    let currentIndex = 0;
    if (streamInterval.current) clearInterval(streamInterval.current);
    streamInterval.current = setInterval(() => {
      if (currentIndex < tokens.length) {
        setStreamedText(prev => prev + tokens[currentIndex]);
        currentIndex++;
      } else {
        clearInterval(streamInterval.current);
        setIsStreaming(false);
      }
    }, 35);
  };

  const handleSendMessage = async (msg) => {
    if ((newMessage.trim() || msg?.trim() || imageBase64) && !isStreaming) {
      setLoadingState("sending-message");
      const message = {
        conversationId: currentConversation?.id,
        message: newMessage ? newMessage : msg || "",
        userId: user?.id,
      };
      setMessages((prev) => ({ totalCount: prev.totalCount + 1, data: [...prev.data, message] }));
      setNewMessage("");
      setImageBase64(null);
      setFileType(null);
      setFileName("");
      const dataTosend = {
        ...message,
        messages: messages.data.map((item) => ({
          role: !item?.userId ? "assistant" : "user",
          content: item?.message,
        })),
        imageString: (imageBase64 === 'doc' || imageBase64 === 'pdf') ? null : imageBase64,
        extraPrompt: extractedText ? `${extractedText}\n\n${msg}` : null
      };
      try {
        const stream = await axiosInstance.post("/chat/send/message", dataTosend);
        const data = stream.data;
        const value = data;
        if (!currentConversation) {
          let newCon = value?.includes("updatedAt")
            ? JSON.parse(value.split("-/-CONVERSATION-/- :").pop())
            : null;
          handleSelectConversation(newCon);
        }
        const assistantResponse = data.split("-/-CONVERSATION-/- :")[0];
        simulateStreaming(assistantResponse);
        setMessages(prev => ({
          totalCount: prev.totalCount + 1,
          data: [...prev.data, { message: assistantResponse, conversationId: 1, userId: null, isLatestMessage: true }],
        }));
        setLoadingState(null);
        setExtractedText(null);
      } catch (error) {
        setLoadingState(null);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  const extractPdfText = async (file) => {
    try {
      return await ExtractDocumentText(file);
    } catch (error) {
      return "Error extracting text from PDF.";
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!validTypes.includes(file.type)) { alert("Only images, PDF, DOC, and DOCX files are allowed."); return; }
    setFileName(file.name);
    if (file.type.startsWith('image/')) {
      setFileType("image");
      const reader = new FileReader();
      reader.onload = (event) => setImageBase64(event.target.result);
      reader.readAsDataURL(file);
    } else if (file.type === 'application/pdf') {
      setFileType("pdf");
      setLoadingState("processing-pdf");
      setImageBase64("pdf");
      const text = await extractPdfText(file);
      setLoadingState(null);
      setExtractedText(text);
    } else {
      setFileType("doc");
      setLoadingState("processing-document");
      setImageBase64("doc");
      const text = await ExtractDocumentText(file);
      setLoadingState(null);
      setExtractedText(text);
    }
  };

  const clearFileAttachment = () => { setImageBase64(null); setFileType(null); setFileName(""); };

  const renderFilePreview = (fileData, fileType, name) => {
    if (fileType === "image" || !fileType) {
      return <img src={fileData} alt="Preview" className="max-h-36 object-contain" />;
    } else if (fileType === "pdf" || fileData === "pdf") {
      return (
        <div className="flex items-center p-2 bg-red-900/30 rounded-md">
          <FaFilePdf className="text-red-400 text-2xl mr-2" />
          <span className="text-sm text-white/70 truncate max-w-[150px]">{name || "PDF Document"}</span>
        </div>
      );
    } else if (fileType === "doc" || fileData === "doc") {
      return (
        <div className="flex items-center p-2 bg-blue-900/30 rounded-md">
          <FaFileWord className="text-blue-400 text-2xl mr-2" />
          <span className="text-sm text-white/70 truncate max-w-[150px]">{name || "Word Document"}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col h-full bg-[#0f1829]">
      {/* Header */}
      {currentConversation && (
        <div className="border-b border-white/10 bg-[#111d42] px-5 py-4">
          <h2 className="ps-8 lg:ps-0 text-xl font-bold text-white">Alfred</h2>
          <p className="ps-8 lg:ps-0 text-sm text-white/40">AI Marketing Operation Manager</p>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 overflow-hidden">
        {!currentConversation ? (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <div className="max-w-md mx-auto w-full">
              {/* Avatar */}
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
                style={{ background: "linear-gradient(135deg, #F05A22, #E8407A)" }}
              >
                <RiRobot2Fill className="text-white text-2xl" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">Alfred</h2>
              <p className="text-white/40 mb-8 text-sm leading-relaxed">
                A virtual assistant designed to assist with marketing operations tasks,
                providing expert advice, troubleshooting, and step-by-step guides.
              </p>

              {/* Chat starters */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {chatStarters?.map((item) => (
                  <div
                    key={item.text}
                    className="bg-[#162035] border border-white/10 p-3 rounded-xl cursor-pointer hover:border-[#F05A22]/40 hover:bg-[#1e2d4a] transition-all text-left"
                    onClick={() => handleSendMessage(item?.text)}
                  >
                    <p className="text-sm text-white/60">{item.text}</p>
                  </div>
                ))}
              </div>

              {/* Input area */}
              <div className="relative">
                {fileType && (
                  <div className="mb-2 relative inline-block">
                    <div className="rounded-lg overflow-hidden border border-white/10">
                      {renderFilePreview(imageBase64, fileType, fileName)}
                    </div>
                    <button
                      onClick={clearFileAttachment}
                      className="absolute top-1 right-1 bg-[#1B2B5E] text-white rounded-full p-1 hover:bg-[#F05A22] transition-colors"
                    >
                      <FaTimes size={12} />
                    </button>
                  </div>
                )}
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Message Alfred..."
                  className="w-full p-3.5 pr-24 border border-white/10 bg-[#162035] text-white placeholder-white/25 rounded-xl resize-none focus:outline-none focus:border-[#F05A22]/40 focus:ring-1 focus:ring-[#F05A22]/20 transition-all text-sm"
                  rows={1}
                />
                <div className="absolute right-3 bottom-2.5 flex items-center gap-1">
                  <label htmlFor="file-upload" className="text-white/30 hover:text-[#F05A22] cursor-pointer transition-colors z-50 p-1">
                    <AiOutlinePaperClip className="text-2xl" />
                    <input id="file-upload" type="file" className="hidden" onChange={handleFile} accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx" />
                  </label>
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={(!newMessage && !imageBase64) || loadingState}
                    className={`p-2.5 rounded-lg transition-all ${(newMessage || imageBase64)
                      ? "text-white"
                      : "bg-white/5 text-white/20 cursor-not-allowed"}`}
                    style={(newMessage || imageBase64) ? { background: "linear-gradient(135deg, #F05A22, #E8407A)" } : {}}
                  >
                    {loadingState ? <FaSpinner className="animate-spin text-sm" /> : <FaPaperPlane className="text-sm" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages?.data?.map((message, index) => {
                const isLastAssistantMessage = !message.userId && index === messages.data.length - 1;
                return (
                  <div
                    key={index}
                    className={`flex items-start gap-3 ${message.userId ? "justify-end" : ""} w-full md:w-[85%] lg:w-[75%] mx-auto`}
                  >
                    {!message.userId && (
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: "linear-gradient(135deg, #F05A22, #E8407A)" }}
                      >
                        <RiRobot2Fill className="text-white text-sm" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] p-4 rounded-2xl text-sm leading-relaxed ${
                        message.userId
                          ? "text-white rounded-tr-sm"
                          : "bg-[#162035] border border-white/10 text-white/80 rounded-tl-sm"
                      }`}
                      style={message.userId ? { background: "linear-gradient(135deg, #1B2B5E, #243875)" } : {}}
                    >
                      {message.imageBase64 && (
                        <div className="mb-3">{renderFilePreview(message.imageBase64, message.fileType, message.fileName)}</div>
                      )}
                      {isLastAssistantMessage && isStreaming ? (
                        <div className="leading-7 msg-main-box" dangerouslySetInnerHTML={{ __html: getProcessedStreamText() }} />
                      ) : (
                        <div className="leading-7 msg-main-box" dangerouslySetInnerHTML={{ __html: marked(message?.message) }} />
                      )}
                      {isLastAssistantMessage && isStreaming && (
                        <span className="inline-block w-1.5 h-4 bg-[#F05A22] ml-1 animate-pulse rounded-full" />
                      )}
                    </div>
                    {message.userId && (
                      <div className="w-8 h-8 rounded-lg bg-[#1e2d4a] border border-white/10 flex items-center justify-center flex-shrink-0">
                        <RiUserFill className="text-white/60 text-sm" />
                      </div>
                    )}
                  </div>
                );
              })}

              {loadingState && (
                <div className="flex items-start gap-3 w-full md:w-[85%] lg:w-[75%] mx-auto">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, #F05A22, #E8407A)" }}
                  >
                    <RiRobot2Fill className="text-white text-sm" />
                  </div>
                  <div className="bg-[#162035] border border-white/10 p-4 rounded-2xl rounded-tl-sm">
                    <div className="flex items-center gap-2 text-sm text-white/50">
                      <FaSpinner className="animate-spin text-[#F05A22]" />
                      <span className="capitalize">{loadingState.replace(/-/g, " ")}...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-white/10 bg-[#111d42] p-4">
              <div className="relative">
                {imageBase64 && (
                  <div className="mb-2 relative inline-block">
                    <div className="rounded-lg overflow-hidden border border-white/10">
                      {renderFilePreview(imageBase64, fileType, fileName)}
                    </div>
                    <button
                      onClick={clearFileAttachment}
                      className="absolute top-1 right-1 bg-[#1B2B5E] text-white rounded-full p-1 hover:bg-[#F05A22] transition-colors"
                    >
                      <FaTimes size={12} />
                    </button>
                  </div>
                )}
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Message Alfred..."
                  className="w-full p-3.5 pr-24 border border-white/10 bg-[#162035] text-white placeholder-white/25 rounded-xl resize-none focus:outline-none focus:border-[#F05A22]/40 focus:ring-1 focus:ring-[#F05A22]/20 transition-all text-sm"
                  rows={1}
                />
                <div className="absolute right-3 bottom-2.5 flex items-center gap-1">
                  <label htmlFor="file-upload-conv" className="text-white/30 hover:text-[#F05A22] cursor-pointer transition-colors z-50 p-1">
                    <AiOutlinePaperClip className="text-2xl" />
                    <input id="file-upload-conv" type="file" className="hidden" onChange={handleFile} accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx" />
                  </label>
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={(!newMessage && !imageBase64) || loadingState || isStreaming}
                    className={`p-2.5 rounded-lg transition-all ${(newMessage || imageBase64) && !isStreaming
                      ? "text-white"
                      : "bg-white/5 text-white/20 cursor-not-allowed"}`}
                    style={(newMessage || imageBase64) && !isStreaming ? { background: "linear-gradient(135deg, #F05A22, #E8407A)" } : {}}
                  >
                    {loadingState || isStreaming ? <FaSpinner className="animate-spin text-sm" /> : <FaPaperPlane className="text-sm" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatComponent;
